/**
 * ImpersonationIndicator — makes it unmistakable that the admin is acting AS
 * another user. Mounted once at the app root. Three always-on signals while
 * impersonating (no top bar, no confirm dialogs — one-click both ways):
 *
 *   1. Amber border framing the entire viewport (ambient, can't scroll away).
 *   2. A fixed top-right identity chip "<target> <- <origin>" + "Return to <origin>".
 *   3. A browser tab-title prefix "[AS <target>] ..." so even a background tab
 *      signals the mode.
 *
 * Reads the impersonation state from localStorage (set by services/impersonation.ts).
 */

import React, { useEffect } from "react";
import { Box, Button, Chip } from "@mui/material";
import {
  getImpersonationInfo,
  returnToAdmin,
} from "../../services/impersonation";

const AMBER = "#f59e0b"; // amber-500

export const ImpersonationIndicator: React.FC = () => {
  const info = getImpersonationInfo();
  const targetName = info?.targetName ?? "";

  // 3. Tab-title prefix while impersonating; restore the prior title on exit.
  useEffect(() => {
    if (!targetName) return;
    const prev = document.title;
    const base = prev.replace(/^\[AS [^\]]*\]\s*/, "");
    document.title = `[AS ${targetName}] ${base}`;
    return () => {
      document.title = base;
    };
  }, [targetName]);

  if (!info) return null;

  return (
    <>
      {/* 1. Amber viewport border — fixed full-viewport overlay, click-through. */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (t) => t.zIndex.tooltip + 1,
          pointerEvents: "none",
          border: `4px solid ${AMBER}`,
        }}
      />
      {/* 2. Identity chip + Return to admin, fixed top-right above everything. */}
      <Box
        role="status"
        sx={{
          position: "fixed",
          top: 8,
          right: 12,
          zIndex: (t) => t.zIndex.tooltip + 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          bgcolor: AMBER,
          color: "#1a1a1a",
          boxShadow: 3,
        }}
      >
        <Chip
          size="small"
          label={`${info.targetName} ⟵ ${info.impersonatorName}`}
          title={`Impersonating ${info.targetName} — you are ${info.impersonatorName}`}
          sx={{
            bgcolor: "rgba(0,0,0,0.12)",
            color: "#1a1a1a",
            fontWeight: 700,
          }}
        />
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={returnToAdmin}
          sx={{
            bgcolor: "#1a1a1a",
            color: AMBER,
            "&:hover": { bgcolor: "#000" },
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          Return to {info.impersonatorName}
        </Button>
      </Box>
    </>
  );
};

export default ImpersonationIndicator;
